import {
  CellClassParams,
  CellDoubleClickedEvent,
  CellKeyDownEvent,
  CellValueChangedEvent,
  ColDef,
  EditableCallbackParams,
  GridApi,
  GridOptions,
  ICellEditorParams,
  RowNode,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core'
import { FormControl, ValidationErrors } from '@angular/forms'
import {
  heliosDefaultColDef,
  heliosDefaultColWidth,
  heliosSizingAdjustments,
} from '@helios/ag-grid-config'
import {
  RootState,
  selectActiveSession,
  selectActiveSessionIdentifier,
  selectActiveSessionReconDate,
  selectActiveUserId,
  selectActiveWindowDate,
  selectIsInternalUser,
  setCommandsMetrics,
} from '@helios/state-management'
import { TelemetryService } from '@helios/telemetry'
import { isNullOrEmpty } from '@helios/utils'
import {
  NgbActiveModal,
  NgbModal,
  NgbModalRef,
} from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { AgGridApi, NfTooltipParams, resizeColumns } from '@northfork/ag-grid'
import {
  InlineEditDataType,
  InlineEditorParams,
  NgSelectOptions,
  getInlineEditColumnType,
} from '@northfork/ag-grid-angular'
import { DisplayDateFormat, ISODateFormat, formatDate } from '@northfork/basics'
import { InlineEditDisplayOptions, ToastService } from '@northfork/ng-basics'
import { isAfter } from 'date-fns'
import { Observable, Subject, of } from 'rxjs'
import {
  catchError,
  debounceTime,
  map,
  retry,
  take,
  takeUntil,
} from 'rxjs/operators'
import {
  BalanceToAdjust,
  BalanceToAdjustKey,
  balanceToAdjustKeys,
} from '../../../model/balance-to-adjust.model'
import { Currency } from '../../../model/currency.model'
import { InventoryType } from '../../../model/inventory-type.model'
import {
  LoanFeeType,
  loanFeeTypeKeys,
} from '../../../model/loan-fee-type.model'
import { AccountDetails } from '../../../model/single-transaction/account-details.model'
import { CashEntryTransactionDetails } from '../../../model/single-transaction/cash-entry/cash-entry-transaction-details'
import {
  CashEntryDataMap,
  CashEntryTransaction,
} from '../../../model/single-transaction/cash-entry/cash-entry-transaction.model'
import {
  CashEntryInputModel,
  CashEntryModel,
  cashField,
} from '../../../model/single-transaction/cash-entry/cash-entry.model'
import { CellMatched } from '../../../model/single-transaction/cell-matched.model'
import { CreateEditTransactionRequest } from '../../../model/single-transaction/create-edit-transaction.model'
import { ExternalTransactionDetail } from '../../../model/single-transaction/external-transaction-detail'
import {
  ExternalDataSourceTypeId,
  ExternalUnmatchedTransaction,
} from '../../../model/single-transaction/external-unmatched-transaction'
import { FeeTypeData } from '../../../model/single-transaction/feeTypeData.model'
import { LotInventoryConfig } from '../../../model/single-transaction/lot-inventory-config.model'
import {
  HistoricalBalanceData,
  TransactionModalInput,
  TransactionModalMode,
} from '../../../model/single-transaction/popup-input.model'
import { SleeveSimpleAccounts } from '../../../model/single-transaction/sleeve-simple-accounts.model'
import { TransactionTypeLabel } from '../../../model/single-transaction/transaction'
import { TransactionDataMapField } from '../../../model/single-transaction/transaction-data-map-field'
import { TransactionMatch } from '../../../model/single-transaction/transaction-match'
import {
  CashEntryValidationView,
  ValidationExternalTransactionData,
} from '../../../model/single-transaction/validation-view.model'
import {
  TranCategory,
  TranCategoryUrl,
} from '../../../model/transaction-category.model'
import {
  TransactionType,
  TransactionTypeEnum,
  TransactionTypeKey,
  transactionTypeKeys,
} from '../../../model/transaction-type.model'
import {
  ErrorSeverity,
  ValidationInfo,
} from '../../../model/validation-response.model'
import { CopyService } from '../../../services/copy.service'
import { CreateEditTransactionService } from '../../../services/create-edit-transaction.service'
import { ModalFocusService } from '../../../services/modal-focus.service'
import { PasteService } from '../../../services/single-transaction/paste.service'
import {
  AdvancedRowsTransaction,
  createCashEntryFromCashEntryInputModel,
  createCashEntryFromExternalTransactionCustody,
  createCashEntryFromExternalTransactionTrading,
  createCashEntryFromHistoricalBalance,
  editCashEntryFromTransaction,
  fetchExternalTransactionCurrencyValues,
  generateAdvancedDataRows,
  generateAdvancedDataRowsForPrepopulated,
  generateCashEntry,
  getCollateralBroker,
} from '../../../util/cash-data-mapper-util'
import {
  ACCOUNT_REQUIRED_ERROR,
  ACCOUNT_REQUIRED_FIELD_WARNING,
  CURRENCY_ONLY_TRANSACTION_TYPES,
  SINGLE_DIALOG_EVENT_NAME,
  SINGLE_DIALOG_VALIDATION_EVENT_NAME,
  activateDialog,
  addWarningDetails,
  clearAllInvalidations,
  createMetadata,
  emptySelectedCellsOnValidKeyPress,
  fillHandleDoubleClickHandlerSingleDialog,
  focusFieldRowEditor,
  getCashEntryType,
  getFieldValidatorFormatter,
  getStringISOForDate,
  getTransactionTypeKeyFromCode,
  handleValidation,
  internalAssetIdFilterGridOptions,
  isDialogComponentInActive,
  prepareMultiViewForMultiMatchValidations,
  singleDialogMetricsDetail,
} from '../../../util/common-utils'
import { TransactionConstants } from '../../../util/constants'
import {
  getExternalLoanFields,
  getLoanFieldValues,
} from '../../../util/loan-field.util'
import {
  getCurrencyFromCurrencyCode,
  getDefaultCurrency,
} from '../../../util/currency-utils'
import {
  createDynamicRowsForCWTransaction,
  createDynamicRowsforExternalTransaction,
} from '../../../util/dynamic-fields'
import {
  isClearwaterCreateTransaction,
  isEditModal,
  isExternalTransaction,
} from '../../../util/popup-input-util'
import transactionDataMapField from '../../../util/transaction-data-map-field.json'
import { getRequestModeFromModalMode } from '../../../util/validation-response-util'

@Component({
  selector: 'hs-cash-entry',
  templateUrl: './cash-entry.component.html',
  styleUrls: ['./cash-entry.component.scss'],
  providers: [PasteService, CopyService],
})
export class CashEntryComponent implements OnInit, OnDestroy {
  // True while an aggregate account is selected but no concrete sub-account has been
  // picked yet. Fields stay editable; validation is deferred and edited fields are flagged.
  aggregateAccountUnresolved = false
  @Input() transInput?: TransactionModalInput
  @Input() prePopulatedInput?: CashEntryInputModel

  gridApi?: AgGridApi
  tradingAccountName?: string
  getFieldValidatorFormatter = getFieldValidatorFormatter
  transactionLoaded = false
  accountDetails?: AccountDetails
  accountDetailsAggregate?: AccountDetails
  accountNameAgg?: string
  accountName?: string
  readonly rowNodeMap = new Map<string, RowNode>() // internal
  conditionalAdvancedRows: CashEntryModel[] = [] // internal
  formSubmitting = false
  rowData: CashEntryModel[] = generateCashEntry(getStringISOForDate(new Date()))
  currencyList: Currency[] = []
  isInternalUser = false
  sleeveSimpleAccount: SleeveSimpleAccounts[] = []
  private readonly transTypes = getCashEntryType().sort(
    (a: TransactionType, b: TransactionType) => a.name.localeCompare(b.name),
  )
  advancedRows: CashEntryModel[] = [] // internal
  sessionId?: string // internal
  accountId?: number
  private userId?: number
  reconDate?: Date // internal
  private windowDate?: Date
  currentTransactionDataField: TransactionDataMapField[] = [] // internal
  feeTypesLoaded = false // internal
  forbiddenErrors = new FormControl('')
  originalTransaction?: CashEntryTransaction // internal
  transactionMatchesCustody?: TransactionMatch[] // internal
  transactionMatchesTrading?: TransactionMatch[] // internal
  hasTransactionMatchesCustody = false // internal
  hasTransactionMatchesTrading = false // internal

  compId = 'cash-' + Math.random()
  activeInstances: NgbModalRef[] = []
  feeTypes: FeeTypeData[] = [] // internal
  isPostDateFuture = false
  isSettleDateFuture = false
  currentSecurityId?: number = getDefaultCurrency().cashSecurityId // internal
  private readonly errorFields: string[] = []
  isDataValid = false
  isCellEditing = false
  isValidating = false
  private cusipValue = ''
  private currencyValue = ''
  noteWarning: ValidationErrors | null = null

  note = new FormControl('')
  displayAdvancedRows = new FormControl(false)
  displayDynamicRows = new FormControl(false)
  clearTransaction = new FormControl(true)

  private executionId?: number
  private externalAccountId?: number
  private isExternalAccountSleeveSimple = false
  originalCusip?: string // internal
  collateralBroker?: string
  isCollateralBrokerAccount = false
  errorDetails: unknown
  gridOptions: GridOptions = this.createGridOptions()
  title = 'Create Cash Entry for account: '
  loadingText = TransactionConstants.LOAD_TRANSACTION
  errorText = ''
  errorHeader: string | undefined
  externalTranTypeLabel?: TransactionTypeLabel
  // loaded from service when creating an external transaction
  private externalTransactionDetail?: ExternalTransactionDetail
  // loaded from service when editing a clearwater transaction
  private cashEntryTransactionDetails?: CashEntryTransactionDetails
  private transactionMatches?: Map<string, TransactionMatch[]>
  private readonly destroySubject$ = new Subject()
  dynamicRows: CashEntryModel[] = []
  propertiesTitle = 'No Table Properties set'

  constructor(
    public activeModal: NgbActiveModal,
    public toastService: ToastService,
    public createEditTransactionService: CreateEditTransactionService,
    readonly store: Store<RootState>,
    private readonly pasteService: PasteService,
    private readonly copyService: CopyService,
    private readonly modalService: NgbModal,
    private readonly elRef: ElementRef,
    private readonly modalFocusService: ModalFocusService,
    private readonly telemetryService: TelemetryService,
  ) {
    this.modalService.activeInstances
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(activeInstances => {
        this.activeInstances = activeInstances
      })
  }

  async ngOnInit() {
    activateDialog(this.activeInstances, this.compId, this.elRef)

    this.modalFocusService
      .outsideFocus(this.elRef)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(() => this.disableGridFocus())

    await this.init()
  }

  ngOnDestroy() {
    this.copyService.destroy()
    this.pasteService.destroy()

    this.destroySubject$.next()
    this.destroySubject$.complete()
  }

  getProperties(): string[] {
    return [
      this.displayAdvancedRows.value
        ? 'Showing Advanced Rows'
        : 'Not Showing Advanced Rows',
    ]
  }

  /** @internal */
  private async init() {
    const startTime = Date.now()
    if (isClearwaterCreateTransaction(this.transInput)) {
      this.transactionLoaded = true
    }
    if (isEditModal(this.transInput)) {
      this.title = this.transInput?.readOnly
        ? 'View Cash Entry for account: '
        : 'Edit Cash Entry for account: '
    }

    await this.loadStoreData()

    this.loadInventoryConfigData()

    this.rowData
      .filter(row => row.field.includes('Date') && row.value != '')
      .forEach(rowFiltered => {
        if (this.reconDate) rowFiltered.value = this.reconDate.toString()
      })

    await this.loadServiceData()

    this.rowData = await this.createRowData()
    //No Need to wait for Fee Type as it is non-essential.Row data changes are been handled within the method.
    //This manipulate Fee Type cell value for edit case.Hance need to be done after row data.
    this.loadFeeTypeData(this.accountId as number)
    this.unclearTransactionInitially()
    this.setAccountDisplayName()
    this.subscribeFormControls()
    this.determineAndPlugCollateral()
    this.setupInitialData(this.rowData)
    this.transactionLoaded = true
    const now = Date.now()
    const type = singleDialogMetricsDetail(this.transInput)
    this.telemetryService.emitCustomEvent(SINGLE_DIALOG_EVENT_NAME, {
      dialogName: 'Cash-Entry-' + type,
      durationMs: now - startTime,
    })

    if (this.gridApi) {
      this.validate(this.gridApi.api)
    }
  }

  /** @internal */
  subscribeFormControls() {
    this.note.valueChanges.pipe(debounceTime(500)).subscribe(() => {
      if (this.gridApi) {
        this.validate(this.gridApi.api)
      }
    })
    this.clearTransaction.valueChanges.pipe(debounceTime(500)).subscribe(() => {
      if (this.gridApi) {
        this.validate(this.gridApi.api)
      }
    })
  }

  /** @internal */
  async loadStoreData() {
    const sessionId$ = this.store
      .select(selectActiveSessionIdentifier)
      .pipe(take(1))
    const accountId$ = this.store.select(selectActiveSession).pipe(
      take(1),
      map(s => s?.loadedAccountIds[0]),
    )
    const userId$ = this.store.select(selectActiveUserId).pipe(take(1))
    const reconDate$ = this.store
      .select(selectActiveSessionReconDate)
      .pipe(take(1))
    const windowDate$ = this.store.select(selectActiveWindowDate).pipe(take(1))
    // Fail closed: if the user slice is not yet populated, treat as external
    // so the internal Asset ID stays hidden (PVAL-25738).
    const isInternalUser$ = this.store.select(selectIsInternalUser).pipe(
      take(1),
      catchError(() => of(false)),
    )

    return Promise.all([
      sessionId$.toPromise(),
      accountId$.toPromise(),
      userId$.toPromise(),
      reconDate$.toPromise(),
      windowDate$.toPromise(),
      isInternalUser$.toPromise(),
    ]).then(
      ([
        sessionId,
        accountId,
        userId,
        reconDate,
        windowDate,
        isInternalUser,
      ]) => {
        this.sessionId = sessionId
        this.accountId = accountId
        this.userId = userId
        this.reconDate = reconDate
        this.windowDate = windowDate
        this.isInternalUser = isInternalUser ?? false
        this.gridApi?.api.onFilterChanged()
      },
    )
  }

  /**
   * PVAL-25738: external users must not see the internal Asset ID row when
   * editing/viewing an existing or external transaction.
   */
  private get hideInternalAssetIdRows(): boolean {
    return (
      !this.isInternalUser &&
      (isEditModal(this.transInput) || isExternalTransaction(this.transInput))
    )
  }

  loadInventoryConfigData() {
    this.createEditTransactionService
      .loadInventoryConfig(this.accountId ?? 0)
      .subscribe(inventoryConfig => {
        this.isCollateralBrokerAccount =
          inventoryConfig.inventoryGroupMethod == 'COLLATERAL_BROKER'
        if (this.isCollateralBrokerAccount) {
          this.determineAndPlugCollateral()
        }
      })
  }

  /** @internal */
  async loadServiceData() {
    const isExTx = isExternalTransaction(this.transInput)
    const isEdit = isEditModal(this.transInput) ?? false
    //All transaction require FeeType Data
    this.loadFeeTypeData(this.accountId as number)
    // All transactions require the currency list to be loaded
    const currencyList$: Observable<Currency[]> =
      this.createEditTransactionService.loadCurrencies()

    // Account details should be loaded if the given transaction is not an external transaction
    let accountDetails$: Observable<AccountDetails | undefined>
    if (!isExTx) {
      accountDetails$ = this.createEditTransactionService.loadAccountDetails(
        this.accountId as number,
      )
    } else {
      accountDetails$ = of(undefined)
    }
    // When not editing and the transaction is an external transaction, we load external transaction details
    let exTxDetail$: Observable<ExternalTransactionDetail | undefined>
    if (!isEdit && isExTx) {
      this.title = 'Post Cash Entry for account: '
      exTxDetail$ =
        this.createEditTransactionService.loadExternalTransactionDetail(
          Number(this.transInput?.transactionId),
          this.accountId as number,
        )
    } else {
      exTxDetail$ = of(undefined)
    }

    // For editing transactions, we load cash entry transaction details and transaction matches
    let cashEntryTransactionDetails$: Observable<
      CashEntryTransactionDetails | undefined
    >
    let transactionMatches$: Observable<
      Map<string, TransactionMatch[]> | undefined
    >
    if (isEdit && !isExTx) {
      cashEntryTransactionDetails$ =
        this.createEditTransactionService.loadCashEntryTransactionDetails(
          this.transInput?.transactionId as string,
          this.accountId as number,
          this.transInput?.securityId as number,
        )
      transactionMatches$ =
        this.createEditTransactionService.loadTransactionMatches(
          this.transInput?.transactionId as string,
        )
    } else {
      cashEntryTransactionDetails$ = of(undefined)
      transactionMatches$ = of(undefined)
    }

    return Promise.all([
      accountDetails$.toPromise(),
      currencyList$.toPromise(),
      exTxDetail$.toPromise(),
      cashEntryTransactionDetails$.toPromise(),
      transactionMatches$.toPromise(),
    ])
      .then(
        ([
          accountDetails,
          currencyList,
          exTxDetail,
          cashEntryTransactionDetails,
          transactionMatches,
        ]) => {
          this.accountDetails = accountDetails
          this.currencyList = currencyList
          this.externalTransactionDetail = exTxDetail
          this.externalTranTypeLabel =
            exTxDetail?.externalTransaction.transactionTypeLabel
          this.cashEntryTransactionDetails = cashEntryTransactionDetails
          this.transactionMatches = transactionMatches
        },
      )
      .catch(err => {
        this.errorText = TransactionConstants.ERROR_LOADING_TRANSACTION
        this.errorDetails = err.error
      })
  }

  async loadServiceDataOnAccountChange(accountId: number) {
    const inventoryConfig$: Observable<LotInventoryConfig> =
      this.createEditTransactionService.loadInventoryConfig(accountId)

    const accountDetails$: Observable<AccountDetails> =
      this.createEditTransactionService.loadAccountDetails(accountId)

    return Promise.all([
      accountDetails$.toPromise(),
      inventoryConfig$.toPromise(),
    ])
      .then(([accountDetails, inventoryConfig]) => {
        this.accountDetails = accountDetails
        this.accountId = accountDetails.accountId
        this.isCollateralBrokerAccount =
          inventoryConfig.inventoryGroupMethod == 'COLLATERAL_BROKER'
        this.determineAndPlugCollateral()
      })
      .catch(error => {
        this.errorDetails = error.error
      })
  }
  /** @internal */
  async createRowData(): Promise<CashEntryModel[]> {
    // PrePopulate the form
    if (this.prePopulatedInput) {
      return this.createRowDataForPrePopulatedInput()
    }

    // Edit an existing transaction
    if (isEditModal(this.transInput)) {
      return await this.createRowDataForEditCash()
    }

    // Create a new external transaction
    if (isExternalTransaction(this.transInput)) {
      return await this.createRowDataForExternalUnmatched()
    }

    if (
      this.transInput?.positionData != undefined &&
      this.transInput.modalMode === TransactionModalMode.CreateFromPosition &&
      'unitDifference' in this.transInput.positionData
    ) {
      this.collateralBroker = this.transInput.positionData.collateralBroker
      return this.createRowDataForHistoricalBalance(
        this.transInput.positionData,
        this.transInput.transType,
      )
    }

    // Create new cash entry
    return this.rowData
  }

  /** @internal */
  createRowDataForHistoricalBalance(
    data: HistoricalBalanceData,
    tranTypeCode?: string,
  ): CashEntryModel[] {
    this.currentSecurityId = data.securityId
    return createCashEntryFromHistoricalBalance(data, tranTypeCode)
  }

  /** @internal */
  async createRowDataForEditCash(): Promise<CashEntryModel[]> {
    const cashEntry = this.cashEntryTransactionDetails
      ?.transactionData as CashEntryTransaction

    if (this.accountDetails?.isAggregate) {
      this.accountDetailsAggregate = this.accountDetails
      await this.loadServiceDataOnAccountChange(cashEntry.accountId)
    }
    this.collateralBroker = cashEntry.dataMap?.collateralBroker
    this.currentSecurityId = cashEntry.securityId ?? 0
    this.originalTransaction = cashEntry
    this.setCurrentTransactionDataField(TransactionTypeEnum[cashEntry.tranType])
    this.transactionMatchesCustody = this.transactionMatches?.get(
      `${ExternalDataSourceTypeId.Custody}`,
    )
    this.transactionMatchesTrading = this.transactionMatches?.get(
      `${ExternalDataSourceTypeId.Trading}`,
    )

    let tradingAccountId = 0
    this.transactionMatchesTrading?.forEach(matches => {
      tradingAccountId = matches.accountId
    })
    if (cashEntry.accountId !== tradingAccountId && tradingAccountId != 0) {
      await this.loadTradingAccountDetail(tradingAccountId)
    }

    this.hasTransactionMatchesCustody = Boolean(this.transactionMatchesCustody)
    this.hasTransactionMatchesTrading = Boolean(this.transactionMatchesTrading)
    this.note.setValue(this.cashEntryTransactionDetails?.note?.note ?? '')
    this.clearTransaction.patchValue(cashEntry.clearedDate ? true : false)
    this.accountName =
      this.accountDetails?.accountId + ' - ' + this.accountDetails?.accountName
    this.accountNameAgg = this.accountDetailsAggregate
      ? this.accountDetailsAggregate?.accountId +
        ' - ' +
        this.accountDetailsAggregate?.accountName
      : undefined
    let combinedCustodyAmount = 0
    let combinedTradingAmount = 0
    if (
      this.transactionMatchesCustody &&
      this.transactionMatchesCustody.length > 1
    ) {
      const custodyColumnDef = this.gridApi?.api.getColumnDef('custody')
      if (custodyColumnDef) {
        custodyColumnDef.headerName = 'Custody (multi)'
      }
      this.transactionMatchesCustody.forEach(
        tranMatch => (combinedCustodyAmount += tranMatch.amount),
      )
    }

    if (
      this.transactionMatchesTrading &&
      this.transactionMatchesTrading.length > 1
    ) {
      const tradingColumnDef = this.gridApi?.api.getColumnDef('trading')
      if (tradingColumnDef) {
        tradingColumnDef.headerName = 'Trading (multi)'
      }
      this.transactionMatchesTrading.forEach(
        tranMatch => (combinedTradingAmount += tranMatch.amount),
      )
    }
    this.generateDynamicRows(
      this.transactionMatchesTrading?.[0],
      this.transactionMatchesCustody?.[0],
    )
    this.gridApi?.api.refreshHeader()
    return editCashEntryFromTransaction(
      this.cashEntryTransactionDetails as CashEntryTransactionDetails,
      this.currencyList,
      this.accountName,
      this.transactionMatchesCustody &&
        this.transactionMatchesCustody.length == 1
        ? this.transactionMatchesCustody[0]
        : undefined,
      this.transactionMatchesTrading &&
        this.transactionMatchesTrading.length == 1
        ? this.transactionMatchesTrading[0]
        : undefined,
      this.accountNameAgg,
      combinedCustodyAmount ? combinedCustodyAmount.toString() : '',
      combinedTradingAmount ? combinedTradingAmount.toString() : '',
      this.tradingAccountName,
    )
  }

  /** @internal */
  async createRowDataForExternalUnmatched(): Promise<CashEntryModel[]> {
    const externalUnmatchedData = this.externalTransactionDetail
      ?.externalTransaction as ExternalUnmatchedTransaction
    this.executionId = externalUnmatchedData.executionId
    this.externalAccountId = externalUnmatchedData.accountId
    this.accountDetails = this.externalTransactionDetail?.accountDetails
    this.collateralBroker = externalUnmatchedData.aggInfoJson?.collateralBroker
    const sleeveAggAccountId =
      this.externalTransactionDetail?.accountDetails?.accountId
    if (
      this.accountDetails?.isAggregate &&
      sleeveAggAccountId &&
      sleeveAggAccountId != this.externalAccountId
    ) {
      this.isExternalAccountSleeveSimple = true
      this.fetchSleeveSimples(sleeveAggAccountId)
      await this.loadServiceDataOnAccountChange(this.externalAccountId)
    }
    this.originalCusip = externalUnmatchedData.assetId
    this.currentSecurityId = externalUnmatchedData.securityId

    if (
      CURRENCY_ONLY_TRANSACTION_TYPES.includes(
        TransactionTypeEnum[externalUnmatchedData.tranType],
      )
    ) {
      const [cusip, currency] = fetchExternalTransactionCurrencyValues(
        externalUnmatchedData,
        this.currencyList,
      )
      this.currentSecurityId = currency.cashSecurityId
    }

    this.setCurrentTransactionDataField(
      TransactionTypeEnum[externalUnmatchedData.tranType],
    )
    this.accountName =
      this.accountDetails?.accountId + ' - ' + this.accountDetails?.accountName
    if (
      externalUnmatchedData.dataSourceTypeId == ExternalDataSourceTypeId.Custody
    ) {
      this.hasTransactionMatchesCustody = true
      this.dynamicRows = createDynamicRowsforExternalTransaction(
        externalUnmatchedData.aggInfoJson,
        'custody',
      ) as CashEntryModel[]
      return createCashEntryFromExternalTransactionCustody(
        externalUnmatchedData,
        this.currencyList,
        this.accountName,
        this.isExternalAccountSleeveSimple,
      )
    }
    this.hasTransactionMatchesTrading = true
    this.dynamicRows = createDynamicRowsforExternalTransaction(
      externalUnmatchedData.aggInfoJson,
      'trading',
    ) as CashEntryModel[]
    return createCashEntryFromExternalTransactionTrading(
      externalUnmatchedData,
      this.currencyList,
      this.accountName,
      this.isExternalAccountSleeveSimple,
    )
  }

  /** @internal */
  async createRowDataForPrePopulatedInput(): Promise<CashEntryModel[]> {
    if (!this.prePopulatedInput?.secId) throw new Error('secId not provided')

    if (this.prePopulatedInput?.fromDistribution) {
      this.collateralBroker = this.prePopulatedInput.collateralBroker ?? ''
      if (this.prePopulatedInput?.accountId)
        await this.loadServiceDataOnAccountChange(
          this.prePopulatedInput.accountId,
        )
    }

    this.prePopulatedInput.note
      ? this.note.setValue(this.prePopulatedInput.note)
      : this.note.setValue(this.cashEntryTransactionDetails?.note?.note ?? '')
    this.currentSecurityId = this.prePopulatedInput.secId
    const tranType = getTransactionTypeKeyFromCode(
      // Defaults to coupon because this is primarily used by coupon transactions (for now)
      this.prePopulatedInput?.tranTypeCode ?? 'CPN',
    )
    this.setCurrentTransactionDataField(TransactionTypeEnum[tranType])

    if (this.prePopulatedInput.prepaidInterest) {
      this.advancedRows = generateAdvancedDataRowsForPrepopulated(
        this.prePopulatedInput,
      )
    }
    return createCashEntryFromCashEntryInputModel(this.prePopulatedInput)
  }

  private createColumnDefs(): ColDef[] {
    return [
      {
        field: cashField('isEditable'),
        hide: true,
      },
      {
        field: cashField('errorMessage'),
        hide: true,
      },
      {
        field: cashField('validityStatus'),
        headerName: '',
        width: heliosDefaultColWidth.ValidityStatusCol,
        valueGetter: () => '',
        cellClass: params => {
          return params.data.validityStatus === 'valid'
            ? 'mdi mdi-check-circle text-success'
            : params.data.validityStatus === 'invalid'
            ? 'mdi mdi-close-circle-outline text-danger'
            : 'mdi mdi-alert-circle-outline text-warning'
        },
        tooltipField: 'errorMessage',
        tooltipComponentParams: {
          showTooltip: params =>
            params?.data.errorMessage != null && params.data.errorMessage != '',
        } as NfTooltipParams,
      },
      {
        field: cashField('field'),
        headerName: 'Field',
        width: 80,
      },
      {
        field: cashField('value'),
        headerName: 'Value',
        type: getInlineEditColumnType(),
        cellEditorParams: (params: ICellEditorParams) =>
          this.editInlineColumns(params),
        valueFormatter: params => this.valueFormatter(params, 'value'),
        editable: (x: EditableCallbackParams) => this.editable(x),
        width: 300,
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
      },
      {
        field: cashField('custody'),
        headerName: 'Custody',
        width: 165,
        cellStyle: (params: CellClassParams) =>
          this.deduceValidityStatusCellClass(
            params,
            this.transactionMatchesCustody &&
              this.transactionMatchesCustody?.length > 1
              ? true
              : false,
          ),
        valueFormatter: params => this.valueFormatter(params, 'custody'),
      },
      {
        field: cashField('trading'),
        headerName: 'Trading',
        width: 165,
        cellStyle: (params: CellClassParams) =>
          this.deduceValidityStatusCellClass(
            params,
            this.transactionMatchesTrading &&
              this.transactionMatchesTrading?.length > 1
              ? true
              : false,
          ),
        valueFormatter: params => this.valueFormatter(params, 'trading'),
      },
    ]
  }

  valueFormatter(params: ValueFormatterParams, colName: string) {
    // Get the row data from the parameters.
    const rowData: CashEntryModel = params.data

    if (rowData.id === 'category' && colName === 'value') {
      return TransactionTypeEnum[rowData.value as TransactionTypeKey]
    }
    // If the type is 'number' and value is not empty, format the value with thousand comma separator.
    else if (rowData.type === 'number' && params.value) {
      return parseFloat(params.value).toLocaleString('en-US', {
        maximumFractionDigits: 20,
      })
    }

    return params.value
  }

  /**
   * get cell class on validityStatus
   *
   * @returns cell class
   */
  deduceValidityStatusCellClass(params: CellClassParams, multiFlag: boolean) {
    return multiFlag && params.data.id != 'amount'
      ? { 'background-color': 'lightgrey' }
      : undefined
  }
  private createGridOptions(): GridOptions {
    return {
      ...heliosSizingAdjustments,
      ...internalAssetIdFilterGridOptions(
        () => this.hideInternalAssetIdRows,
        () => this.rowNodeMap,
      ),
      onGridReady: event => {
        this.gridApi = event
        resizeColumns(event.api, event.columnApi)
        this.copyService.registerGrid(event.api)
        this.pasteService.registerGrid(event.api, event.columnApi)
      },
      defaultColDef: {
        ...heliosDefaultColDef,
        menuTabs: [],
        minWidth: heliosDefaultColWidth.ValidityStatusCol,
        sortable: false,
      },
      sideBar: false,
      enableCellChangeFlash: true,
      enableRangeSelection: true,
      suppressMultiRangeSelection: false,
      enableFillHandle: true,
      fillHandleDirection: 'y',
      onCellEditingStarted: () => {
        this.isCellEditing = true
      },
      onCellEditingStopped: () => {
        this.isCellEditing = false
      },
      onCellKeyDown: (event: CellKeyDownEvent) => {
        if (event.api.getEditingCells().length == 0) {
          emptySelectedCellsOnValidKeyPress(
            (event.event as KeyboardEvent).key,
            event.api,
          )
          const categoryNode = this.rowNodeMap.get('category')
          if (categoryNode?.data.value == '') {
            handleValidation(categoryNode, 'INVALID', 'Valid Value Required')
            this.isDataValid = false
          }
        }
      },
      getRowNodeId: ({ id }: CashEntryModel) => id,
      columnDefs: this.createColumnDefs(),
      onCellDoubleClicked: (event: CellDoubleClickedEvent) => {
        if (
          event.column.getColId() === 'custody' ||
          event.column.getColId() === 'trading'
        ) {
          const rowNode = event.api.getRowNode(event.data['id'])
          if (
            event.data[event.column.getColId()] != '' &&
            rowNode?.data.isEditable
          ) {
            rowNode?.setDataValue('value', event.data[event.column.getColId()])
          }
          if (rowNode != null) {
            event.api.redrawRows({ rowNodes: [rowNode] })
          }
        } else if (event.column.getColId() === 'value') {
          const element = event.event?.target as HTMLElement
          if (element.className == 'ag-fill-handle') {
            fillHandleDoubleClickHandlerSingleDialog(event.api)
          } else {
            event.api.startEditingCell({
              rowIndex: event.rowIndex ?? 0,
              colKey: event.column.getColId(),
            })
          }
        }
      },
      onCellValueChanged: (event: CellValueChangedEvent) => {
        this.onColumnValueChanged(event)
      },
    }
  }

  editable(params: EditableCallbackParams): boolean {
    const tableNode = params.api?.getRowNode(params.data['id'])
    if (tableNode) {
      this.rowNodeMap.set(params.data['id'], tableNode)
    }
    // A custom (fee-master) loanFeeType has no matching enum option, so it can't be picked from
    // the dropdown — show it read-only. The 4 legacy enum values stay editable (GAL-5173).
    if (
      params.data['id'] === 'loanFeeType' &&
      params.data['value'] &&
      !(Object.values(LoanFeeType) as string[]).includes(params.data['value'])
    ) {
      return false
    }
    // Fields stay editable before an account is picked; validation is deferred instead, and
    // edited rows are flagged until the account is resolved (see onColumnValueChanged).
    return params.data.isEditable
  }

  /* Define dataType for all fields */
  editInlineColumns(params: ICellEditorParams): InlineEditorParams {
    switch (params.data.id) {
      case 'postDate':
      case 'settleDate':
      case 'entryDate':
        return {
          dataType: InlineEditDataType.Date,
        }
      case 'category':
        return {
          dataType: InlineEditDataType.Select,
          options: {
            items: this.transTypes,
            bindLabel: 'name',
            bindValue: 'name',
          } as NgSelectOptions,
        }
      case 'currency':
        return {
          dataType: InlineEditDataType.Select,
          options: {
            items: this.currencyList,
            bindValue: 'code',
            bindLabel: 'code',
          },
        }
      case 'account':
        return {
          dataType: InlineEditDataType.Select,
          options: {
            items: this.sleeveSimpleAccount,
            bindValue: 'displayLabel',
            bindLabel: 'displayLabel',
            loading: this.sleeveSimpleAccount.length == 0,
          },
        }
      case 'inventoryType':
        return {
          dataType: InlineEditDataType.Select,
          options: {
            items: Object.values(InventoryType),
          },
        }
      case 'feeType':
        return {
          dataType: InlineEditDataType.Select,
          options: {
            items: this.feeTypes,
            bindLabel: 'name',
            bindValue: 'name',
            loading: !this.feeTypesLoaded,
          },
        }
      case 'amount':
        return {
          dataType: InlineEditDataType.Number,
          options: {
            format: '1.2-16',
          },
          disablePopover: true,
        }
      case 'cusip':
        return {
          dataType: InlineEditDataType.String,
          disablePopover: true,
        }
      case 'foreignTaxOriginalEventDate':
        return {
          dataType: InlineEditDataType.Date,
        }
      case 'navAffect':
      case 'fundedCommitmentAffect':
      case 'recallableCommitmentAffect':
      case 'totalCommitmentAffect':
      case 'exitFee':
      case 'exitFeeReceivable':
      case 'prepaymentPenaltyFee':
      case 'delinquencyFee':
      case 'modificationFee':
      case 'miscellaneousFee':
      case 'interestBearingInterestReserve':
      case 'interestBearingPrincipalReserve':
      case 'nonInterestBearingInterestReserve':
      case 'nonInterestBearingPrincipalReserve':
        return {
          dataType: InlineEditDataType.Number,
          options: {
            format: '1.2-6',
          },
          disablePopover: true,
        }
      case 'isCapitalize':
        return {
          dataType: InlineEditDataType.Select,
          options: {
            items: ['true', 'false'],
          },
        }
      case 'loanFeeType':
        return {
          dataType: InlineEditDataType.Select,
          options: {
            items: Object.values(LoanFeeType),
          },
        }
      case 'balanceToAdjust':
        return {
          dataType: InlineEditDataType.Select,
          options: {
            items: Object.values(BalanceToAdjust),
          },
        }
      default:
        return {
          dataType: InlineEditDataType.String,
          disablePopover: true,
        }
    }
  }

  /**
   * Toggle advanced rows.
   * This is a callback triggered by the html template.
   */
  toggleRows() {
    if (this.displayAdvancedRows.value) {
      if (this.conditionalAdvancedRows.length == 0) {
        this.conditionalAdvancedRows = this.getTransactionDataMapField(
          this.rowNodeMap.get('category')?.data.value,
          this.originalTransaction?.dataMap,
        )
      }
      if (this.advancedRows.length == 0) {
        this.advancedRows = this.getAdvancedRows(
          this.rowNodeMap.get('category')?.data.value,
        )
      }
      this.applyRowTransaction()
    } else {
      this.removeRowTransaction()
    }
    this.updatePropertiesTitle()
  }

  /** Seeds the advanced rows from the external transaction when there is no saved transaction yet. */
  private getAdvancedRowsTransaction(): AdvancedRowsTransaction | undefined {
    const external = this.externalTransactionDetail?.externalTransaction
    if (this.originalTransaction || !external) {
      return this.originalTransaction
    }
    const { balanceToAdjust, ...loanFields } = getExternalLoanFields(
      external,
      external.aggInfoJson,
    )
    return {
      dataMap: {
        ...loanFields,
        balanceToAdjust: balanceToAdjust as BalanceToAdjustKey,
      },
    }
  }

  getAdvancedRows(currentCategory: string) {
    let prepaidInterestNumberValue = undefined
    let externalDataSource = undefined

    if (isExternalTransaction(this.transInput)) {
      prepaidInterestNumberValue =
        this.externalTransactionDetail?.externalTransaction?.prepaidInterest

      externalDataSource =
        this.externalTransactionDetail?.externalTransaction?.dataSourceTypeId
    } else {
      prepaidInterestNumberValue =
        this.originalTransaction?.dataMap?.prepaidInterest
    }

    return generateAdvancedDataRows(
      this.feeTypes,
      currentCategory,
      this.getAdvancedRowsTransaction(),
      this.transactionMatchesCustody &&
        this.transactionMatchesCustody.length == 1
        ? this.transactionMatchesCustody[0].inventoryType
        : '',
      this.transactionMatchesTrading &&
        this.transactionMatchesTrading.length == 1
        ? this.transactionMatchesTrading[0].inventoryType
        : '',
      prepaidInterestNumberValue,
      externalDataSource === 1 ? prepaidInterestNumberValue : undefined,
      externalDataSource === 2 ? prepaidInterestNumberValue : undefined,
    )
  }

  applyRowTransaction() {
    if (this.conditionalAdvancedRows) {
      this.advancedRows.push(...this.conditionalAdvancedRows)
    }
    this.gridApi?.api.applyTransaction({ add: this.advancedRows })
  }

  removeRowTransaction() {
    const selectedData: RowNode[] = []
    this.advancedRows.push(...this.conditionalAdvancedRows)
    this.advancedRows.forEach(elements => {
      const rowNode = this.gridApi?.api.getRowNode(elements.id)
      if (rowNode != null) {
        selectedData.push(rowNode)
      }
    })
    this.conditionalAdvancedRows = []
    this.advancedRows = []
    this.gridApi?.api.applyTransaction({ remove: selectedData })
  }

  async onColumnValueChanged(event: CellValueChangedEvent) {
    if (
      event.newValue === event.oldValue ||
      event.column.getColId() != 'value'
    ) {
      return
    }

    const cashEntry: CashEntryModel = event.data
    let value = cashEntry.value
    const data: CashEntryModel[] = this.getTransactionDataMapField(
      value,
      this.originalTransaction?.dataMap,
    )
    const categoryNode = this.rowNodeMap.get('category')
    const currencyNode = this.rowNodeMap.get('currency')
    const cusipNode = this.rowNodeMap.get('cusip')
    switch (cashEntry.id) {
      case 'category':
        /* Category onChange event handling */
        this.conditionalAdvancedRows = []
        this.setCurrentTransactionDataField(value)
        if (
          CURRENCY_ONLY_TRANSACTION_TYPES.includes(
            value as TransactionTypeEnum,
          ) &&
          !currencyNode?.data.value
        ) {
          currencyNode?.setDataValue('value', getDefaultCurrency().code)
        }

        /* If toggle is already clicked then remove extra data first and apply */
        if (this.displayAdvancedRows.value) {
          this.removeRowTransaction()
          this.conditionalAdvancedRows.push(...data)
          this.advancedRows.push(
            ...generateAdvancedDataRows(
              this.feeTypes,
              categoryNode?.data.value,
              this.getAdvancedRowsTransaction(),
            ),
          )
          this.applyRowTransaction()
        } else {
          if (this.advancedRows.length <= 0) {
            this.advancedRows.push(
              ...this.getAdvancedRows(categoryNode?.data.value),
            )
          }
          this.conditionalAdvancedRows.push(...data)
        }

        /* Check if transaction type is eligible to edit currency */
        if (!isEditModal(this.transInput)) {
          if (
            CURRENCY_ONLY_TRANSACTION_TYPES.includes(
              value as TransactionTypeEnum,
            )
          ) {
            if (currencyNode) {
              currencyNode.setDataValue('isEditable', true)
              event.api.redrawRows({ rowNodes: [currencyNode] })
            }
            if (cusipNode && currencyNode) {
              cusipNode.setDataValue('isEditable', false)
              event.api.redrawRows({ rowNodes: [cusipNode] })
              this.handleCurrency(currencyNode.data['value'], cusipNode)
            }
          } else {
            if (cusipNode) {
              cusipNode.setDataValue('isEditable', true)
              event.api.redrawRows({ rowNodes: [cusipNode] })
            }
            if (currencyNode) {
              currencyNode.setDataValue('isEditable', false)
              event.api.redrawRows({ rowNodes: [currencyNode] })
            }
          }
        }
        break
      case 'cusip':
        value = value.toUpperCase()
        cusipNode?.setDataValue('value', value)
        if (this.cusipValue != value) {
          this.cusipValidator(event, currencyNode)
        }
        break
      case 'postDate':
      case 'settleDate':
        this.clearUnclearTransaction(value, cashEntry.id)
        break
      case 'currency':
        if (this.currencyValue != value) {
          this.handleCurrency(event.data['value'], cusipNode)
        }
        break
      case 'account':
        this.loadAggAccountDetails(event.data['value'])
        break
      case 'feeType':
        if (this.originalTransaction)
          this.originalTransaction.accountFeeType = this.feeTypes.filter(
            feeType => feeType.name == event.data['value'],
          )[0].id
        break
      case 'inventoryType':
        if (this.originalTransaction)
          this.originalTransaction.inventoryType = event.data['value']
        break
      case 'navAffect':
        if (this.originalTransaction?.dataMap)
          this.originalTransaction.dataMap.navAffect = event.data['value']
        break

      case 'fundedCommitmentAffect':
        if (this.originalTransaction?.dataMap)
          this.originalTransaction.dataMap.fundedCommitmentAffect =
            event.data['value']
        break
      case 'foreignTaxOriginalEventDate':
        if (this.originalTransaction?.dataMap)
          this.originalTransaction.dataMap.foreignTaxOriginalEventDate =
            event.data['value']
        break
      case 'totalCommitmentAffect':
        if (this.originalTransaction?.dataMap)
          this.originalTransaction.dataMap.totalCommitmentAffect =
            event.data['value']
        break
      case 'isCapitalize':
        if (this.originalTransaction?.dataMap) {
          this.originalTransaction.dataMap.isCapitalize = event.data['value']
        }
        break
      case 'loanFeeType':
        if (this.originalTransaction?.dataMap) {
          this.originalTransaction.dataMap.loanFeeType = event.data['value']
        }
        break
      case 'balanceToAdjust':
        if (this.originalTransaction?.dataMap) {
          this.originalTransaction.dataMap.balanceToAdjust = event.data['value']
        }
        break
      case 'exitFee':
        if (this.originalTransaction?.dataMap) {
          this.originalTransaction.dataMap.exitFee = event.data['value']
        }
        break
      case 'exitFeeReceivable':
        if (this.originalTransaction?.dataMap) {
          this.originalTransaction.dataMap.exitFeeReceivable =
            event.data['value']
        }
        break
      case 'prepaymentPenaltyFee':
        if (this.originalTransaction?.dataMap) {
          this.originalTransaction.dataMap.prepaymentPenaltyFee =
            event.data['value']
        }
        break
      case 'delinquencyFee':
        if (this.originalTransaction?.dataMap) {
          this.originalTransaction.dataMap.delinquencyFee = event.data['value']
        }
        break
      case 'modificationFee':
        if (this.originalTransaction?.dataMap) {
          this.originalTransaction.dataMap.modificationFee = event.data['value']
        }
        break
      case 'miscellaneousFee':
        if (this.originalTransaction?.dataMap) {
          this.originalTransaction.dataMap.miscellaneousFee =
            event.data['value']
        }
        break
      case 'interestBearingInterestReserve':
        if (this.originalTransaction?.dataMap) {
          this.originalTransaction.dataMap.interestBearingInterestReserve =
            event.data['value']
        }
        break
      case 'interestBearingPrincipalReserve':
        if (this.originalTransaction?.dataMap) {
          this.originalTransaction.dataMap.interestBearingPrincipalReserve =
            event.data['value']
        }
        break
      case 'nonInterestBearingInterestReserve':
        if (this.originalTransaction?.dataMap) {
          this.originalTransaction.dataMap.nonInterestBearingInterestReserve =
            event.data['value']
        }
        break
      case 'nonInterestBearingPrincipalReserve':
        if (this.originalTransaction?.dataMap) {
          this.originalTransaction.dataMap.nonInterestBearingPrincipalReserve =
            event.data['value']
        }
        break
      case 'prepaidInterest':
        if (this.originalTransaction?.dataMap) {
          this.originalTransaction.dataMap.prepaidInterest = event.data['value']
        }
    }
    if ('account' != cashEntry.id) {
      if (this.aggregateAccountUnresolved) {
        // No account yet: flag the edited field instead of validating it.
        addWarningDetails(event.node, ACCOUNT_REQUIRED_FIELD_WARNING)
        event.api.refreshCells({ force: true, rowNodes: [event.node] })
      } else {
        await this.validate(event.api)
      }
    }
  }

  async loadAggAccountDetails(accountDisplayName: string) {
    const account = this.sleeveSimpleAccount.find(
      name => name.displayLabel == accountDisplayName,
    )
    if (account) {
      await this.loadServiceDataOnAccountChange(account.id)
      // A concrete account is now resolved; clear the account-unresolved marks and validate.
      this.aggregateAccountUnresolved = false
      clearAllInvalidations(this.gridApi?.api)
      if (this.gridApi) {
        this.validate(this.gridApi.api)
      }
    }
  }

  private handleCurrency(
    currencyCode: string | undefined,
    cusipNode: RowNode | undefined,
  ) {
    const currency = getCurrencyFromCurrencyCode(
      currencyCode,
      this.currencyList,
    )
    if (!currency) {
      return
    }
    this.currentSecurityId = currency.cashSecurityId
    if (cusipNode) {
      cusipNode.setDataValue('value', currency.cusip)
      this.cusipValue = currency.cusip
    }
  }

  async validate(api: GridApi) {
    const startTime = Date.now()
    const categoryNode = this.rowNodeMap.get('category')
    if (!categoryNode?.data.value) {
      return
    }
    // Don't validate while an aggregate account is unresolved; there's no concrete
    // account to scope the request to yet (edited fields are flagged instead).
    if (this.aggregateAccountUnresolved) {
      return
    }
    this.isDataValid = false
    const validateView = this.createValidationView(
      this.accountDetails as AccountDetails,
    )
    this.isValidating = true
    try {
      const validationInfos = await this.createEditTransactionService
        .validateTransactionTradeCash(validateView, TranCategoryUrl.CASH_ENTRY)
        .toPromise()
      if (
        this.isCollateralBrokerAccount &&
        !(validateView.userData.transaction as CashEntryTransaction).dataMap
          ?.collateralBroker
      ) {
        validationInfos.push({
          message: 'Collateral Broker must be specified',
          level: ErrorSeverity.ERROR,
          impactedFields: ['collateralBroker'],
        })
      }
      if (
        this.rowNodeMap.get('category')?.data.value ===
          TransactionTypeEnum.CASH_BALANCE_FIELD_ADJUSTMENT &&
        !(validateView.userData.transaction as CashEntryTransaction).dataMap
          ?.balanceToAdjust
      ) {
        validationInfos.push({
          message:
            'Balance To Adjust is required for Cash Balance Field Adjustment transactions',
          level: ErrorSeverity.ERROR,
          impactedFields: ['balanceToAdjust'],
        })
      }
      this.handleValidationResponse(
        validationInfos?.filter(
          validationInfo => validationInfo.level != ErrorSeverity.FORBIDDEN,
        ),
        api,
      )
      this.handleValidationResponseForbidden(
        validationInfos.filter(
          validationInfo => validationInfo.level == ErrorSeverity.FORBIDDEN,
        ),
      )
      this.isValidating = false
      const now = Date.now()
      const type = singleDialogMetricsDetail(this.transInput)
      this.telemetryService.emitCustomEvent(
        SINGLE_DIALOG_VALIDATION_EVENT_NAME,
        {
          dialogName: 'Cash-Entry-' + type,
          durationMs: now - startTime,
        },
      )
    } catch (error) {
      this.errorHeader = TransactionConstants.VALIDATION_ERROR
      this.errorDetails = error.error
      this.isValidating = false
    }
  }

  handleValidationResponseForbidden(validationInfos: ValidationInfo[]) {
    if (validationInfos.length == 0) return
    const forbiddenErrorsList: string[] = []
    validationInfos.forEach(validationInfo => {
      forbiddenErrorsList.push(validationInfo.message)
    })
    const validatorKey = 'forbiddenErrorsValidator'
    this.forbiddenErrors.markAsTouched()
    this.forbiddenErrors.setErrors({
      [validatorKey]: {
        message: forbiddenErrorsList,
      },
    })
    this.isDataValid = false
  }

  handleValidationResponse(validationInfos: ValidationInfo[], api: GridApi) {
    const errorMessages: Map<string, string[]> = new Map()
    const warningMessages: Map<string, string[]> = new Map()
    validationInfos.forEach(validationInfo => {
      if (validationInfo.level == ErrorSeverity.ERROR) {
        validationInfo.impactedFields.forEach(impactedField => {
          if (errorMessages.has(impactedField)) {
            errorMessages.get(impactedField)?.push(validationInfo.message)
          } else {
            errorMessages.set(impactedField, [validationInfo.message])
          }
        })
      } else {
        validationInfo.impactedFields.forEach(impactedField => {
          if (warningMessages.has(impactedField)) {
            warningMessages.get(impactedField)?.push(validationInfo.message)
          } else {
            warningMessages.set(impactedField, [validationInfo.message])
          }
        })
      }
    })
    this.isDataValid = errorMessages.size == 0
    this.rowNodeMap.forEach((value: RowNode) => {
      handleValidation(value, 'VALID', '')
    })
    this.note.setErrors(null)
    errorMessages.forEach((value: string[], key: string) => {
      let currentNode = this.rowNodeMap.get(key)
      if (key == 'securityId') currentNode = this.rowNodeMap.get('cusip')
      if (key == 'tranType') currentNode = this.rowNodeMap.get('category')
      if (key == 'accountId') currentNode = this.rowNodeMap.get('account')
      if (currentNode) {
        handleValidation(currentNode, 'INVALID', value.join(' | '))
      } else if (key == 'transactionNote') {
        const validatorKey = 'transactionNoteValidator'
        this.note.markAsTouched()
        this.note.setErrors({
          [validatorKey]: {
            message: value,
          },
        })
      }
    })
    warningMessages.forEach((value: string[], key: string) => {
      let currentNode = this.rowNodeMap.get(key)
      if (key == 'securityId') currentNode = this.rowNodeMap.get('cusip')
      if (key == 'tranType') currentNode = this.rowNodeMap.get('category')
      if (key == 'accountId') currentNode = this.rowNodeMap.get('account')
      if (currentNode && currentNode.data.errorMessage == '') {
        handleValidation(currentNode, 'WARNING', value.join(' | '))
      } else if (
        key == 'transactionNote' &&
        !errorMessages.has('transactionNote')
      ) {
        const validatorKey = 'transactionNoteValidator'
        this.noteWarning = {
          [validatorKey]: {
            message: value,
          },
        }
      }
    })
  }

  createValidationView(
    accountDetails: AccountDetails,
  ): CashEntryValidationView {
    const view: CashEntryValidationView = {
      userData: {
        transaction: this.prepareTransactionData('value', true),
        cusip: this.rowNodeMap.get('cusip')?.data.value,
      },
      accountDetails: accountDetails,
      requestMode: getRequestModeFromModalMode(this.transInput),
      windowDate: this.windowDate ?? new Date(),
      reconDate: this.reconDate ?? new Date(),
    }

    if (this.note.value != '') {
      view.transactionNote = this.note.value
    }
    if (this.hasTransactionMatchesCustody) {
      view.custodyData =
        this.transactionMatchesCustody &&
        this.transactionMatchesCustody.length > 1
          ? prepareMultiViewForMultiMatchValidations(
              this.transactionMatchesCustody,
            )
          : [
              {
                transaction: this.prepareTransactionData('custody', false),
                cusip: this.rowNodeMap.get('cusip')?.data.custody,
              },
            ]
    }
    if (this.hasTransactionMatchesTrading) {
      view.tradingData =
        this.transactionMatchesTrading &&
        this.transactionMatchesTrading.length > 1
          ? prepareMultiViewForMultiMatchValidations(
              this.transactionMatchesTrading,
            )
          : [
              {
                transaction: this.prepareTransactionData('trading', false),
                cusip: this.rowNodeMap.get('cusip')?.data.trading,
              },
            ]
    }
    if (this.externalAccountId) {
      view.externalTransaction = {
        accountId: this.externalAccountId,
        isSleeveSimple: this.isExternalAccountSleeveSimple,
      } as ValidationExternalTransactionData
    }
    return view
  }

  setupInitialData(rowData: CashEntryModel[]) {
    rowData.forEach(element => {
      if (element.validityStatus === 'invalid') {
        this.errorFields.push(element.id)
        this.isDataValid = false
      }
      const tableNode = this.rowNodeMap.get(element.id)

      if (tableNode) {
        if (
          tableNode.data.validityStatus == 'invalid' &&
          element.validityStatus == 'valid'
        ) {
          this.errorFields.splice(
            this.errorFields.indexOf(tableNode.data['id']),
            1,
          )
          if (this.errorFields.length == 0) {
            this.isDataValid = true
          }
        }
        tableNode.data = element
        this.gridApi?.api.redrawRows({ rowNodes: [tableNode] })
      }
    })
  }

  loadFeeTypeData(accountId: number) {
    this.feeTypesLoaded = false
    this.createEditTransactionService.loadFeeAccrual(accountId).subscribe(
      (feeTypesObj: { [s: string]: string }) => {
        this.feeTypes = this.createFeeTypes(feeTypesObj)
        this.handleFeeTypes()
        this.feeTypesLoaded = true
      },
      error => {
        this.errorDetails = error.error
      },
    )
  }

  createFeeTypes(feeTypesObj: { [s: string]: string }): FeeTypeData[] {
    return Object.entries(feeTypesObj).map(([key, value]) => {
      return {
        id: Number(key),
        name: value,
      }
    })
  }

  handleFeeTypes() {
    this.feeTypes.forEach(({ id, name }: FeeTypeData) => {
      if (
        this.originalTransaction?.accountFeeType &&
        this.originalTransaction?.accountFeeType == id
      ) {
        this.rowNodeMap.get('feeType')?.setDataValue('value', name)
      }
    })
  }

  cusipValidator(
    event: CellValueChangedEvent,
    currencyNode: RowNode | undefined,
  ) {
    if (event.data['value']) {
      this.errorDetails = ''
      this.createEditTransactionService
        .fetchSecurityIdFromCusip([event.data['value']])
        .subscribe(
          data => {
            if (data && data.length != 0) {
              this.currentSecurityId = data[0].id
              if (currencyNode) {
                currencyNode.setDataValue('value', data[0].currencyCode)
                this.currencyValue = data[0].currencyCode
              }
            }
          },
          err => {
            this.errorHeader = TransactionConstants.ERROR_LOADING_SECURITY
            this.errorDetails = err.error
            this.gridApi?.api.getRowNode('cusip')?.setDataValue('value', '')
          },
        )
    }
  }

  getTransactionDataMapField(
    currentCategory: string,
    cashEntryDataMap?: CashEntryDataMap,
  ): CashEntryModel[] {
    const transData = getCashEntryType().filter(
      data => data.name == currentCategory,
    )
    let data: TransactionDataMapField[] = transactionDataMapField.filter(
      element =>
        element.applicableTransType?.includes(
          transData.length > 0 ? transData[0].code : 'TRN',
        ),
    )
    if (transData[0] && transData[0].code == 'FTW') data = []
    return this.createTransactionDataMapRows(data, cashEntryDataMap)
  }

  private setCurrentTransactionDataField(currentCategory: string) {
    const transData = getCashEntryType().filter(
      data => data.name == currentCategory,
    )
    this.currentTransactionDataField = transactionDataMapField.filter(element =>
      element.applicableTransType?.includes(
        transData.length > 0 ? transData[0].code : 'TRN',
      ),
    )
  }

  private createTransactionDataMapRows(
    data: TransactionDataMapField[],
    cashEntryDataMap?: CashEntryDataMap,
  ): CashEntryModel[] {
    const result: CashEntryModel[] = []
    data.forEach(dataMap => {
      const key = dataMap.name as keyof CashEntryDataMap
      const mapValue = cashEntryDataMap?.[key]

      const value =
        (() => {
          switch (key) {
            case 'foreignTaxOriginalEventDate':
              return mapValue != null
                ? formatDate(mapValue, ISODateFormat, DisplayDateFormat)
                : ''
            default:
              return mapValue != null ? String(mapValue) : ''
          }
        })() ?? ''

      result.push({
        id: key,
        validityStatus: 'valid',
        isEditable: true,
        type: 'string',
        field: dataMap.displayName,
        value: value,
        custody: '',
        trading: '',
        errorMessage: '',
      })
    })
    return result
  }

  save() {
    if (!this.isDataValid || !this.sessionId) {
      return
    }
    this.store.dispatch(
      setCommandsMetrics({
        command: 'cashEntry',
        time: Date.now(),
      }),
    )
    this.formSubmitting = true
    this.loadingText = TransactionConstants.SAVE_TRANSACTION
    let unclearTransaction = false
    if (this.originalTransaction?.clearedDate && !this.clearTransaction.value) {
      unclearTransaction = true
    }
    const tranCommand: CreateEditTransactionRequest = {
      transaction: this.prepareTransactionData('value', false),
      tranCategory: TranCategory.CASH_ENTRY,
      metadata: createMetadata(
        this.reconDate ?? new Date(),
        this.rowNodeMap.get('cusip')?.data.value,
        unclearTransaction,
      ),
    }
    if (this.note.value != '') {
      tranCommand.transactionNote = { newValue: this.note.value }
    }
    if (this.originalTransaction?.transactionId) {
      tranCommand.notes =
        TransactionConstants.HELIOS_CASH_ENTRY_SAVE_NOTE_CW_EDIT
      this.createEditTransactionService
        .editTransaction(
          this.sessionId,
          this.originalTransaction.transactionId,
          tranCommand,
        )
        .subscribe(
          () => {
            this.toastService.success(
              'Clearwater Transaction edit command successfully submitted.',
            )
            this.formSubmitting = false
            this.closeModal()
          },
          error => {
            this.errorHeader = TransactionConstants.ERROR_SAVING_TRANSACTION
            this.errorDetails = error.error
            this.formSubmitting = false
          },
        )
    } else {
      if (isExternalTransaction(this.transInput)) {
        tranCommand.notes =
          TransactionConstants.HELIOS_CASH_ENTRY_SAVE_NOTE_EXTERNAL
        tranCommand.extTran = {
          externalAccountId: this.externalAccountId ?? 0,
          executionId: this.executionId ?? 0,
          externalTranId: Number(this.transInput?.transactionId),
          originalCusip: this.originalCusip,
          status: this.transInput?.externalTransactionStatus,
        }

        if (this.externalTranTypeLabel?.id) {
          tranCommand.transaction.typeLabel = {
            id: this.externalTranTypeLabel.id,
            label: this.externalTranTypeLabel.label,
            description: this.externalTranTypeLabel.description,
          }
        }

        this.createEditTransactionService
          .saveExternalMatch(this.sessionId, tranCommand)
          .subscribe(
            () => {
              this.toastService.success(
                'External Transaction command successfully submitted.',
              )
              this.formSubmitting = false
              this.closeModal()
            },
            error => {
              this.errorHeader = TransactionConstants.ERROR_SAVING_TRANSACTION
              this.errorDetails = error.error
              this.formSubmitting = false
            },
          )
      } else {
        tranCommand.notes =
          TransactionConstants.HELIOS_CASH_ENTRY_SAVE_NOTE_CW_CREATE
        this.createEditTransactionService
          .createTransaction(this.sessionId, tranCommand)
          .subscribe(
            () => {
              this.toastService.success(
                'Clearwater Transaction create command successfully submitted.',
              )
              this.formSubmitting = false
              this.closeModal()
            },
            error => {
              this.errorHeader = TransactionConstants.ERROR_SAVING_TRANSACTION
              this.errorDetails = error.error
              this.formSubmitting = false
            },
          )
      }
    }
  }

  prepareTransactionData(
    column: 'value' | 'custody' | 'trading',
    isValidate: boolean,
  ): CashEntryTransaction {
    const collateralNode = this.rowNodeMap.get('collateralBroker')
    let createdDate = new Date().toISOString()
    if (isEditModal(this.transInput) && this.originalTransaction?.createdDate) {
      createdDate = new Date(
        this.originalTransaction?.createdDate,
      ).toISOString()
    }

    const transactionData: CashEntryTransaction = {
      postDate: this.rowNodeMap.get('postDate')?.data[column],
      amount: Number(this.rowNodeMap.get('amount')?.data[column]),
      tranType: transactionTypeKeys.filter(
        tranKey =>
          TransactionTypeEnum[tranKey] ==
          this.rowNodeMap.get('category')?.data[column],
      )[0],
      createdDate: createdDate,
      modifiedDate: new Date().toISOString(),
      active: true,
      userId: this.userId ?? 0,
      sourceId: TransactionConstants.HELIOS_SOURCE_ID,
      accountId: this.accountId as number,
      entryDate: this.rowNodeMap.get('entryDate')?.data[column],
      settleDate: this.rowNodeMap.get('settleDate')?.data[column],
      dataMap:
        collateralNode?.data.value != ''
          ? { collateralBroker: collateralNode?.data.value }
          : {},
      revisionId: isEditModal(this.transInput)
        ? (this.originalTransaction?.revisionId ?? 0) + 1
        : 1,
    }

    if (!isValidate) {
      transactionData.securityId = this.currentSecurityId
    }
    if (this.currentTransactionDataField.length > 0) {
      const dataMap = new Map()
      if (!this.displayAdvancedRows.value) {
        const dataMapField = this.getTransactionDataMapField(
          this.rowNodeMap.get('category')?.data[column],
          this.originalTransaction?.dataMap,
        )
        dataMapField.forEach(data => {
          const val = data.value
          if (val != '') {
            switch (data.id) {
              case 'foreignTaxOriginalEventDate': {
                const formattedDate = formatDate(
                  val,
                  DisplayDateFormat,
                  ISODateFormat,
                ) // AWS wants the date here to be MM/dd/yyyy
                dataMap.set(data.id, formattedDate)
                break
              }
              default:
                dataMap.set(data.id, Number(val))
                break
            }
          }
        })
      } else {
        this.currentTransactionDataField.forEach(data => {
          const val = this.rowNodeMap.get(data.name)?.data[column]
          if (val != '') {
            switch (data.name) {
              case 'foreignTaxOriginalEventDate': {
                const formattedDate = formatDate(
                  val,
                  DisplayDateFormat,
                  ISODateFormat,
                ) // AWS wants the date here to be MM/dd/yyyy
                dataMap.set(data.name, formattedDate)
                break
              }
              default:
                dataMap.set(data.name, Number(val))
                break
            }
          }
        })
      }
      transactionData.dataMap = Object.fromEntries(dataMap)
    }
    if (collateralNode?.data.value != '' && transactionData.dataMap) {
      transactionData.dataMap.collateralBroker = collateralNode?.data.value
    }

    if (!this.displayAdvancedRows.value) {
      if (isExternalTransaction(this.transInput)) {
        const prepaidValue =
          this.externalTransactionDetail?.externalTransaction?.prepaidInterest
        if (prepaidValue && transactionData.dataMap) {
          transactionData.dataMap.prepaidInterest = prepaidValue
        }
      } else {
        const prepaidValue = this.originalTransaction?.dataMap?.prepaidInterest
        if (prepaidValue && transactionData.dataMap) {
          transactionData.dataMap.prepaidInterest = prepaidValue
        }
      }
    } else {
      const prepaidInterestNode = this.rowNodeMap.get('prepaidInterest')
      if (
        prepaidInterestNode?.data?.value &&
        prepaidInterestNode.data.value != '' &&
        prepaidInterestNode.data.value &&
        transactionData.dataMap
      ) {
        transactionData.dataMap.prepaidInterest = Number(
          prepaidInterestNode.data.value,
        )
      }
      const exitFeeNode = this.rowNodeMap.get('exitFee')
      if (
        exitFeeNode?.data?.value &&
        !isNullOrEmpty(exitFeeNode.data.value) &&
        transactionData.dataMap
      ) {
        transactionData.dataMap.exitFee = Number(exitFeeNode.data.value)
      }
      const exitFeeReceivableNode = this.rowNodeMap.get('exitFeeReceivable')
      if (
        exitFeeReceivableNode?.data?.value &&
        !isNullOrEmpty(exitFeeReceivableNode.data.value) &&
        transactionData.dataMap
      ) {
        transactionData.dataMap.exitFeeReceivable = Number(
          exitFeeReceivableNode.data.value,
        )
      }
      const prepaymentPenaltyFeeNode = this.rowNodeMap.get(
        'prepaymentPenaltyFee',
      )
      if (
        prepaymentPenaltyFeeNode?.data?.value &&
        !isNullOrEmpty(prepaymentPenaltyFeeNode.data.value) &&
        transactionData.dataMap
      ) {
        transactionData.dataMap.prepaymentPenaltyFee = Number(
          prepaymentPenaltyFeeNode.data.value,
        )
      }
      const delinquencyFeeNode = this.rowNodeMap.get('delinquencyFee')
      if (
        delinquencyFeeNode?.data?.value &&
        !isNullOrEmpty(delinquencyFeeNode.data.value) &&
        transactionData.dataMap
      ) {
        transactionData.dataMap.delinquencyFee = Number(
          delinquencyFeeNode.data.value,
        )
      }
      const modificationFeeNode = this.rowNodeMap.get('modificationFee')
      if (
        modificationFeeNode?.data?.value &&
        !isNullOrEmpty(modificationFeeNode.data.value) &&
        transactionData.dataMap
      ) {
        transactionData.dataMap.modificationFee = Number(
          modificationFeeNode.data.value,
        )
      }
      const miscellaneousFeeNode = this.rowNodeMap.get('miscellaneousFee')
      if (
        miscellaneousFeeNode?.data?.value &&
        !isNullOrEmpty(miscellaneousFeeNode.data.value) &&
        transactionData.dataMap
      ) {
        transactionData.dataMap.miscellaneousFee = Number(
          miscellaneousFeeNode.data.value,
        )
      }
      if (transactionData.dataMap) {
        Object.assign(
          transactionData.dataMap,
          getLoanFieldValues(field => this.rowNodeMap.get(field)?.data?.value),
        )
      }
    }
    if (
      !CURRENCY_ONLY_TRANSACTION_TYPES.includes(
        this.rowNodeMap.get('category')?.data[column] as TransactionTypeEnum,
      )
    ) {
      const inventoryNode = this.rowNodeMap.get('inventoryType')
      if (inventoryNode && inventoryNode.data[column] != '') {
        transactionData.inventoryType =
          Object.keys(InventoryType)[
            Object.values(InventoryType).indexOf(inventoryNode.data[column])
          ]
      } else if (!this.displayAdvancedRows.value) {
        transactionData.inventoryType = this.originalTransaction?.inventoryType
      }
    }

    const feeNode = this.rowNodeMap.get('feeType')
    if (feeNode?.data[column] && feeNode.data[column] != '') {
      transactionData.accountFeeType = this.feeTypes.filter(
        feeType => feeType.name == feeNode.data[column],
      )[0].id
    } else if (!this.displayAdvancedRows.value) {
      transactionData.accountFeeType = this.originalTransaction?.accountFeeType
    }
    if (this.clearTransaction.value) {
      transactionData.clearedDate = isValidate
        ? new Date().toISOString().split('Z')[0]
        : new Date().toISOString()
    }
    if (this.originalTransaction) {
      transactionData.transactionId = this.originalTransaction.transactionId
      transactionData.legacyOrigTranId =
        this.originalTransaction.legacyOrigTranId
      transactionData.legacyTranId = this.originalTransaction.legacyTranId
    }
    if (!isValidate && this.originalTransaction?.typeLabelId) {
      transactionData.typeLabelId = this.originalTransaction.typeLabelId
    }
    const isCapitalizeNode = this.rowNodeMap.get('isCapitalize')
    if (isCapitalizeNode?.data[column] != null) {
      const isCapitalize = isCapitalizeNode?.data[column] === 'true'
      transactionData.isCapitalize = isCapitalize
      transactionData.dataMap = Object.assign(transactionData.dataMap ?? {}, {
        isCapitalize,
      })
    }
    const loanFeeType = this.rowNodeMap.get('loanFeeType')
    if (
      loanFeeType?.data[column] &&
      loanFeeType.data[column] != '' &&
      transactionData.dataMap
    ) {
      const loanFeeTypeValue = loanFeeType.data[column]
      // Map the enum label back to its key; keep a custom (fee-master) name as-is (GAL-5173).
      transactionData.dataMap.loanFeeType =
        loanFeeTypeKeys.filter(
          tranKey => LoanFeeType[tranKey] == loanFeeTypeValue,
        )[0] ?? loanFeeTypeValue
    }

    const balanceToAdjustNode = this.rowNodeMap.get('balanceToAdjust')
    if (
      balanceToAdjustNode?.data[column] &&
      balanceToAdjustNode.data[column] != '' &&
      transactionData.dataMap
    ) {
      transactionData.dataMap.balanceToAdjust = balanceToAdjustKeys.filter(
        (tranKey: BalanceToAdjustKey) =>
          BalanceToAdjust[tranKey] ==
          this.rowNodeMap.get('balanceToAdjust')?.data[column],
      )[0]
    }

    return transactionData
  }

  clearUnclearTransaction(date: string, nodeId: 'postDate' | 'settleDate') {
    if (!this.prePopulatedInput?.fromDistribution) {
      if (
        isAfter(
          new Date(date),
          this.reconDate
            ? new Date(this.reconDate as unknown as string)
            : new Date(),
        )
      ) {
        this.clearTransaction.patchValue(false)
      } else {
        if (
          (nodeId == 'postDate' && !this.isSettleDateFuture) ||
          (nodeId == 'settleDate' && !this.isPostDateFuture)
        ) {
          this.clearTransaction.patchValue(true)
        }
      }
    }
  }

  determineAndPlugCollateral() {
    if (
      this.isCollateralBrokerAccount &&
      !this.rowData.find(row => row.id == 'collateralBroker')
    ) {
      const collateralRow = getCollateralBroker(this.collateralBroker)
      this.rowData?.push(collateralRow)
      this.gridApi?.api.applyTransaction({
        add: [collateralRow],
      })
    }
  }

  private closeModal() {
    this.activeModal.close()
  }

  fetchSleeveSimples(accountId: number) {
    const sleeveSimples$ = this.createEditTransactionService
      .fetchSubAccounts(accountId)
      .pipe(retry(2))
    sleeveSimples$.subscribe(
      account => {
        this.sleeveSimpleAccount = account
        // Open the picker only once its options exist (opening earlier leaves the
        // select stuck loading). Skip when an external post already filled the account.
        if (
          this.aggregateAccountUnresolved &&
          this.gridApi?.api &&
          !this.isExternalAccountSleeveSimple
        ) {
          focusFieldRowEditor(this.gridApi.api, 'account')
        }
      },
      error => {
        this.errorHeader = TransactionConstants.ERROR_LOAD_SLEEVE_ACCOUNT
        this.errorDetails = error.error
      },
    )
  }

  unclearTransactionInitially() {
    if (this.prePopulatedInput?.fromDistribution) {
      this.clearTransaction.patchValue(false)
      return
    }
    const postDate = this.rowData.find(row => row.id == 'postDate')?.value ?? ''
    const settleDate =
      this.rowData.find(row => row.id == 'settleDate')?.value ?? ''
    this.isPostDateFuture = isAfter(
      new Date(postDate),
      this.reconDate
        ? new Date(this.reconDate as unknown as string)
        : new Date(),
    )
    this.isSettleDateFuture = isAfter(
      new Date(settleDate),
      this.reconDate
        ? new Date(this.reconDate as unknown as string)
        : new Date(),
    )
    if (this.isPostDateFuture || this.isSettleDateFuture) {
      this.clearTransaction.patchValue(false)
    }
  }
  setAccountDisplayName() {
    const accountRowField = this.rowData?.find(row => row.id == 'account')
    // Clear an aggregate account and force a sub-account pick, unless a sleeve-simple post
    // already resolved it to its concrete sub-account.
    if (
      this.accountDetails?.isAggregate &&
      !this.isExternalAccountSleeveSimple
    ) {
      if (accountRowField) {
        accountRowField.isEditable = true
        accountRowField.value = ''
        accountRowField.trading = ''
        accountRowField.custody = ''
        // Flag the account row so it's obvious it must be filled before validation.
        accountRowField.validityStatus = 'invalid'
        accountRowField.errorMessage = ACCOUNT_REQUIRED_ERROR
      }
      this.aggregateAccountUnresolved = true
      this.gridApi?.api.refreshCells({ force: true })
      this.fetchSleeveSimples(this.accountDetails?.accountId)
      this.resetAggAccountDetails()
    } else if (accountRowField) {
      accountRowField.value =
        this.accountDetails?.accountId +
        ' - ' +
        this.accountDetails?.accountName
    }
  }

  resetAggAccountDetails() {
    this.accountId = undefined
    this.accountDetails = undefined
  }

  disableGridFocus() {
    this.gridApi?.api.clearRangeSelection()
  }

  matchCellDoubleClick(copiedData: CellMatched) {
    this.rowNodeMap
      .get(copiedData.field)
      ?.setDataValue('value', copiedData.value)
  }

  @HostListener('click') dialogClicked() {
    if (isDialogComponentInActive(this.compId)) {
      activateDialog(this.activeInstances, this.compId, this.elRef)
    }
  }

  getComponentId() {
    return this.compId
  }

  async loadTradingAccountDetail(tradingAccountId?: number) {
    if (tradingAccountId) {
      const tradingAccountDetailPromise$ =
        this.createEditTransactionService.loadAccountDetails(tradingAccountId)
      return Promise.all([tradingAccountDetailPromise$.toPromise()]).then(
        async ([tradingAccountDetail]) => {
          this.tradingAccountName =
            tradingAccountDetail.accountId +
            '-' +
            tradingAccountDetail.accountName
        },
      )
    }
  }

  toggleDynamicRows() {
    if (this.displayDynamicRows.value) {
      this.applyDynamicRowTransaction()
    } else {
      this.removeDynamicRowTransaction()
    }
    this.updatePropertiesTitle()
  }

  updatePropertiesTitle() {
    let title = ''
    title += this.displayDynamicRows.value ? 'Showing Dynamic Rows. ' : ''
    if (this.displayAdvancedRows.value) title += 'Showing Advanced Rows. '

    if (title === '') title = 'No Table Properties set'

    this.propertiesTitle = title
  }

  applyDynamicRowTransaction() {
    this.gridApi?.api.applyTransaction({ add: this.dynamicRows })
  }

  removeDynamicRowTransaction() {
    this.gridApi?.api.applyTransaction({ remove: this.dynamicRows })
  }

  generateDynamicRows(
    matchTrading?: TransactionMatch,
    matchCustody?: TransactionMatch,
  ) {
    if (!matchTrading && !matchCustody) {
      return
    }
    const custodyAggInfo = matchCustody?.aggInfoString
      ? JSON.parse('{ ' + matchCustody?.aggInfoString + ' }')
      : undefined
    const tradingAggInfo = matchTrading?.aggInfoString
      ? JSON.parse('{ ' + matchTrading?.aggInfoString + ' }')
      : undefined

    this.dynamicRows = createDynamicRowsForCWTransaction(
      custodyAggInfo || {},
      tradingAggInfo || {},
    ) as CashEntryModel[]
  }
}
